import { useEffect, useRef, useState } from "react";
import {
  ChatWidgetText,
  isSafeWidgetUrl,
  OfferWidget,
} from "../../../Helper/ChatWidget";
import { CheckIcon, CopyIcon } from "../ChatWidgetIcons";
import {
  Button,
  Card,
  Eyebrow,
  Foot,
  openWidgetUrl,
} from "../ChatWidgetPrimitives";
import "./OfferCard.scss";

/** Clipboard API first; the hidden-textarea copy where it's blocked (iframes). */
const copyText = async (value: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (error) {
    // Permission denied inside the iframe: fall through.
  }
  try {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const isCopied = document.execCommand("copy");
    document.body.removeChild(area);
    return isCopied;
  } catch (error) {
    return false;
  }
};

/** 15 Offer: a code the shop lets Kai give. Copy (client only) and Apply. */
export const OfferCard = ({
  widget,
  text,
}: {
  widget: OfferWidget;
  text: ChatWidgetText;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const codeRef = useRef<HTMLSpanElement>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const copy = async () => {
    const isDone = await copyText(widget.code);
    if (!isDone) {
      // Nothing worked: select the code so a long-press / Cmd+C still copies.
      const selection = window.getSelection();
      if (codeRef.current && selection) {
        const range = document.createRange();
        range.selectNodeContents(codeRef.current);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      return;
    }
    setIsCopied(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setIsCopied(false), 2400);
  };

  return (
    <Card className="cw-offer" label={widget.headline}>
      <div className="cw-pad">
        <div className="cw-row">
          <Eyebrow>{widget.title ?? text("widgetYourOffer")}</Eyebrow>
          {widget.expires && (
            <span className="cw-muted cw-sm">
              {text("widgetEndsOn", { date: widget.expires })}
            </span>
          )}
        </div>
        <div className="cw-h cw-h--xl cw-offer-headline">{widget.headline}</div>
        <div className="cw-offer-code">
          <span ref={codeRef} className="cw-offer-code-value">
            {widget.code}
          </span>
          <button
            type="button"
            className="cw-offer-copy"
            aria-label={
              isCopied
                ? text("widgetCopied")
                : text("widgetCopyCode", { code: widget.code })
            }
            onClick={copy}
          >
            {isCopied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
            <span aria-live="polite">
              {isCopied ? text("widgetCopied") : text("widgetCopy")}
            </span>
          </button>
        </div>
      </div>
      {isSafeWidgetUrl(widget.applyUrl) && (
        <Foot flush>
          <Button onClick={() => openWidgetUrl(widget.applyUrl)}>
            {text("widgetApplyToBag")}
          </Button>
        </Foot>
      )}
    </Card>
  );
};
